import type { CheckItem } from "../viewModelTypes";

export type CheckGroupName = "General Checks" | "Footing Checks" | "Workbook Checks";

export type CheckGroup = {
  name: CheckGroupName;
  checks: CheckItem[];
};

const WORKBOOK_SOURCE = "supporting_workbook_verification";

const GROUP_ORDER: readonly CheckGroupName[] = [
  "General Checks",
  "Footing Checks",
  "Workbook Checks",
];

/**
 * Maps a check to its display group (first match wins): the FOOTING kind takes
 * priority, then the workbook source, with VISUAL and everything else folded
 * into "General Checks". The VISUAL branch stays ahead of the workbook-source
 * branch so a VISUAL check with the workbook source still lands in General
 * Checks (kind wins over source).
 */
export function checkGroupName(check: CheckItem): CheckGroupName {
  if (check.kind === "FOOTING") {
    return "Footing Checks";
  }
  if (check.kind === "VISUAL") {
    return "General Checks";
  }
  if (check.source === WORKBOOK_SOURCE) {
    return "Workbook Checks";
  }
  return "General Checks";
}

function sortGeneralChecks(checks: CheckItem[]): CheckItem[] {
  const visualRank = (check: CheckItem) => (check.kind === "VISUAL" ? 1 : 0);
  return checks
    .map((check, index) => ({ check, index }))
    .sort((a, b) => visualRank(a.check) - visualRank(b.check) || a.index - b.index)
    .map((entry) => entry.check);
}

/**
 * Partitions a section's checks into ordered, non-empty groups. Groups follow a
 * stable display order, and check order within a group is preserved — except in
 * "General Checks", where a stable sort places plain/SIMPLE checks before VISUAL
 * checks while keeping the original relative order within each rank.
 */
export function groupChecks(checks: readonly CheckItem[]): CheckGroup[] {
  return GROUP_ORDER.map((name) => {
    const matched = checks.filter((check) => checkGroupName(check) === name);
    return {
      name,
      checks: name === "General Checks" ? sortGeneralChecks(matched) : matched,
    };
  }).filter((group) => group.checks.length > 0);
}
